// DST-safe timezone helpers powered by luxon.
// All math is delegated to luxon's DateTime.setZone(), which handles DST
// transitions, ambiguous local times, and historical offsets automatically.

import { DateTime, Interval } from "luxon";

const BUSINESS = { start: 9, end: 17 };
const MARGINAL_EARLY = { start: 7, end: 9 };
const MARGINAL_LATE = { start: 17, end: 20 };
const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // luxon: 1=Mon..7=Sun

// Returns the "zoned parts" of `now` in IANA timezone `tz`.
// Backwards-compatible shape with the previous Intl-based helper.
export function getZonedParts(tz, date = new Date()) {
  try {
    const dt = DateTime.fromJSDate(date, { zone: tz });
    if (!dt.isValid) return null;
    return {
      hour: dt.hour,
      minute: dt.minute,
      second: dt.second,
      day: dt.toFormat("dd"),
      month: dt.toFormat("LL"),
      year: String(dt.year),
      weekday: WEEKDAY_SHORT[dt.weekday - 1], // e.g. "Mon"
      weekday_iso: dt.weekday, // 1..7
      time: dt.toFormat("HH:mm"),
      date: dt.toFormat("dd/LL/yyyy"),
      abbr: dt.toFormat("ZZZZ"), // e.g. "CET", "JST", "GMT+5:30"
    };
  } catch {
    return null;
  }
}

// Hours offset between `tz` and `ams` at the given instant.
// Returns string like "+6h" / "−7h" / "+5h30m".
export function offsetFromAmsterdam(tz, ams = "Europe/Amsterdam", date = new Date()) {
  try {
    const a = DateTime.fromJSDate(date, { zone: tz });
    const b = DateTime.fromJSDate(date, { zone: ams });
    if (!a.isValid || !b.isValid) return "—";
    const diffMin = a.offset - b.offset; // luxon: offset is in minutes from UTC
    const sign = diffMin >= 0 ? "+" : "−";
    const abs = Math.abs(diffMin);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return m === 0 ? `${sign}${h}h` : `${sign}${h}h${m}m`;
  } catch {
    return "—";
  }
}

// Availability bucket.
// `tz`           — IANA timezone of the entity
// `opts.weekend` — array like ["Sat","Sun"] (defaults Sat+Sun)
// `opts.holidays`— Set of "YYYY-MM-DD" strings in the entity's local date
// `opts.workingHours` — { start: 9, end: 17 }
// Returns { level, label_es, color, reason }
export function availabilityForZone(tz, opts = {}, date = new Date()) {
  const parts = getZonedParts(tz, date);
  if (!parts) return { level: "inactive", label_es: "—", color: "#64748b", reason: "" };

  const weekend = opts.weekend || ["Sat", "Sun"];
  const holidays = opts.holidays || new Set();
  const wh = opts.workingHours || BUSINESS;
  const hour = parts.hour;
  const dayKey = `${parts.year}-${parts.month}-${parts.day}`;

  // Weekend check (highest priority after holidays)
  const isWeekend = weekend.includes(parts.weekday);
  const isHoliday = holidays.has(dayKey);

  if (isHoliday) {
    return { level: "inactive", label_es: "Festivo", label_en: "Holiday", color: "#ef4444", reason: "Festivo local" };
  }
  if (isWeekend) {
    return {
      level: "inactive", label_es: "Fin de semana", label_en: "Weekend", color: "#ef4444",
      reason: `Fin de semana (${weekend.join("+")})`,
    };
  }
  if (hour >= wh.start && hour < wh.end) {
    return { level: "active", label_es: "Activo", label_en: "Active", color: "#10b981", reason: "Horario laboral" };
  }
  if (
    (hour >= MARGINAL_EARLY.start && hour < wh.start) ||
    (hour >= wh.end && hour < MARGINAL_LATE.end)
  ) {
    return { level: "marginal", label_es: "Marginal", label_en: "Marginal", color: "#eab308", reason: "Fuera de horario principal" };
  }
  return { level: "inactive", label_es: "Inactivo", label_en: "Inactive", color: "#ef4444", reason: "Fuera de horario" };
}

// Backwards-compat (hour-only) so older code keeps compiling.
export function availabilityFromHour(hour) {
  if (hour >= BUSINESS.start && hour < BUSINESS.end) {
    return { level: "active", label_es: "Activo", label_en: "Active", color: "#10b981" };
  }
  if (
    (hour >= MARGINAL_EARLY.start && hour < MARGINAL_EARLY.end) ||
    (hour >= MARGINAL_LATE.start && hour < MARGINAL_LATE.end)
  ) {
    return { level: "marginal", label_es: "Marginal", label_en: "Marginal", color: "#eab308" };
  }
  return { level: "inactive", label_es: "Inactivo", label_en: "Inactive", color: "#ef4444" };
}

// Meeting planner: find 30-min slots in the next `days` days where ALL given
// timezones are simultaneously in business hours, not weekend, not holiday.
// `tzs` can be either:
//   ["Europe/Amsterdam", "Asia/Tokyo"]
// or an array of { tz, weekend?, holidays?, workingHours? } for finer control.
export function findMeetingSlots(tzs, options = {}) {
  const { stepMin = 30, days = 7, startDate = new Date() } = options;
  if (!tzs || tzs.length === 0) return { slots: [], days_checked: 0 };

  const configs = tzs.map((x) => (typeof x === "string" ? { tz: x } : x));
  const startDT = DateTime.fromJSDate(startDate);
  const blocks = [];
  let daysChecked = 0;

  for (let d = 0; d < days; d++) {
    daysChecked = d + 1;
    // Window in UTC = [startDT + d days, startDT + (d+1) days]
    const dayStart = startDT.plus({ days: d });
    // Snap first iteration to next stepMin
    let cursor = d === 0
      ? dayStart.plus({ minutes: stepMin - (dayStart.minute % stepMin) }).set({ second: 0, millisecond: 0 })
      : dayStart.startOf("day");
    const cursorEnd = cursor.plus({ days: 1 });

    let currentBlock = null;
    while (cursor < cursorEnd) {
      const ok = configs.every((c) => {
        const a = availabilityForZone(c.tz, {
          weekend: c.weekend,
          holidays: c.holidays,
          workingHours: c.workingHours || BUSINESS,
        }, cursor.toJSDate());
        return a.level === "active";
      });
      if (ok) {
        if (!currentBlock) {
          currentBlock = { start: cursor.toJSDate(), end: cursor.plus({ minutes: stepMin }).toJSDate() };
        } else {
          currentBlock.end = cursor.plus({ minutes: stepMin }).toJSDate();
        }
      } else if (currentBlock) {
        blocks.push(currentBlock);
        currentBlock = null;
      }
      cursor = cursor.plus({ minutes: stepMin });
    }
    if (currentBlock) blocks.push(currentBlock);
    if (blocks.length > 0) break; // first available day
  }

  return { slots: blocks, days_checked: daysChecked };
}

// Format a Date as "HH:MM" in a given timezone (DST-aware).
export function formatTimeInTz(date, tz) {
  try {
    return DateTime.fromJSDate(date, { zone: tz }).toFormat("HH:mm");
  } catch {
    return "—";
  }
}

// Common IANA timezones for dropdowns — curated subset used as fallback.
const CURATED_TIMEZONES = [
  // Europa
  "Europe/Amsterdam", "Europe/Madrid", "Europe/London", "Europe/Paris", "Europe/Berlin",
  "Europe/Rome", "Europe/Lisbon", "Europe/Athens", "Europe/Istanbul", "Europe/Moscow",
  // América
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "America/Vancouver", "America/Mexico_City", "America/Bogota",
  "America/Sao_Paulo", "America/Buenos_Aires", "America/Lima", "America/Santiago",
  // Asia
  "Asia/Dubai", "Asia/Tehran", "Asia/Karachi", "Asia/Kolkata", "Asia/Dhaka",
  "Asia/Bangkok", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Shanghai",
  "Asia/Tokyo", "Asia/Seoul", "Asia/Manila", "Asia/Jakarta",
  // África
  "Africa/Lagos", "Africa/Cairo", "Africa/Johannesburg", "Africa/Casablanca", "Africa/Nairobi",
  // Oceanía
  "Australia/Sydney", "Australia/Perth", "Pacific/Auckland",
];

// Full IANA list when the browser supports it (Chrome 99+, Firefox 100+, Safari 15.4+).
// Falls back to the curated list on older browsers.
export const IANA_TIMEZONES = (() => {
  try {
    if (typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function") {
      const full = Intl.supportedValuesOf("timeZone");
      if (full && full.length > 0) return full;
    }
  } catch {
    // ignore — fall through to curated
  }
  return CURATED_TIMEZONES;
})();
